import React, { FunctionComponent, memo, useCallback, useRef } from "react";
import { Grid, FormControlLabel, RadioGroup, Radio, Box } from "@mui/material";
import InvestorRequestFormTitle from "@investorRequest/components/InvestorRequestFormTitle";
import { Input } from "@shared/components";
import FieldTooltip from "@investorRequest/components/FieldTooltip";
import { FIELD_TITLES } from "@investorRequest/constants/fieldTooltips";
import { validationMessages } from "@investorRequest/constants/validationMessages";
import { stripLeadingWhitespace } from "@investorRequest/utils/SpecificValidations";

interface Props {
  yesNoValue: string; // "yes" | "no" | ""
  line1: string;
  line2: string;
  onChangeYesNo(value: string): void;
  onChangeLine1(value: string): void;
  onChangeLine2(value: string): void;
  showErrors: boolean;
}

const FurtherCreditFields: FunctionComponent<Props> = ({
  yesNoValue,
  line1,
  line2,
  onChangeYesNo,
  onChangeLine1,
  onChangeLine2,
  showErrors,
}) => {
  const radioTopRef = useRef<HTMLDivElement | null>(null);
  const fieldsStartRef = useRef<HTMLDivElement | null>(null);

  const handleChangeLine1 = useCallback(
    (value: string) => onChangeLine1(stripLeadingWhitespace(value)),
    [onChangeLine1]
  );
  const handleChangeLine2 = useCallback(
    (value: string) => onChangeLine2(stripLeadingWhitespace(value)),
    [onChangeLine2]
  );

  // Auto-scroll functionality disabled - user will scroll manually
  // useEffect(() => {
  //   if (yesNoValue === "yes") {
  //     fieldsStartRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  //   }

  //   if (yesNoValue === "no") {
  //     radioTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  //   }
  // }, [yesNoValue]);

  return (
    <>
      <InvestorRequestFormTitle text='Do these wire instructions contain "For Further Credit" information? (Yes/No)' />
      <Grid item xs={12}>
        <div ref={radioTopRef} />
        <RadioGroup row value={yesNoValue || ""}>
          <FormControlLabel
            value="yes"
            control={<Radio size="small" />}
            label="Yes"
            onClick={() => onChangeYesNo("yes")}
          />
          <FormControlLabel
            value="no"
            control={<Radio size="small" />}
            label="No"
            onClick={() => onChangeYesNo("no")}
          />
        </RadioGroup>
      </Grid>
      {yesNoValue === "yes" && (
        <>
          <div ref={fieldsStartRef} />
          <Grid item xs={12}>
            <FieldTooltip title={FIELD_TITLES.FurtherCreditLine1}>
              <Input
                dense={false}
                placeholder="*For Further Credit - Line 1"
                variant="light"
                size="large"
                value={line1}
                onChange={handleChangeLine1}
                error={showErrors && !line1}
              />
            </FieldTooltip>
            {showErrors && !line1 && (
              <Box mt={1} color="warning.main" fontSize={12}>
                {validationMessages.furtherCredit.line1.required}
              </Box>
            )}
            {showErrors && !!line1 && line1.length > 40 && (
              <Box mt={1} color="warning.main" fontSize={12}>
                Max 40 characters
              </Box>
            )}
          </Grid>
          <Grid item xs={12}>
            <FieldTooltip title={FIELD_TITLES.FurtherCreditLine2}>
              <Input
                dense={false}
                placeholder="For Further Credit - Line 2"
                variant="light"
                size="large"
                value={line2}
                onChange={handleChangeLine2}
                error={false}
              />
            </FieldTooltip>
            {showErrors && !!line2 && line2.length > 40 && (
              <Box mt={1} color="warning.main" fontSize={12}>
                Max 40 characters
              </Box>
            )}
          </Grid>
        </>
      )}
    </>
  );
};

export default memo(FurtherCreditFields);
